<template>
  <div>
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceCategoryOptions"
        :title="'Service Category'"
        :fieldName="'serviceCategories'"
        :loading="serviceCategoryOptionsLoading"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceTypeOptionsSortedAlphabetically"
        :title="'Service Component'"
        :fieldName="'serviceTypes'"
        :loading="serviceTypeOptionsLoading"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="agreementTypeOptions"
        :title="'Agreement Type'"
        :fieldName="'agreementTypes'"
        :loading="agreementTypeOptionsLoading"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceStaffLeadNames"
        :title="'Staff Lead'"
        :fieldName="'staffLeads'"
        :loading="serviceStaffLeadLoading"
        :onlyOneAllowed="false"
        :key="radioButtonFacetKey"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceStatusOptions"
        :title="'Service Status'"
        :fieldName="'serviceStatuses'"
        :loading="serviceStatusOptionsLoading"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="localityOptions"
        :title="'Locality'"
        :fieldName="'localities'"
        :loading="localityLoading"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceReviewsOptions"
        :title="'Reviews'"
        :fieldName="'reviews'"
        :loading="serviceReviewsOptionsLoading"
        :onlyOneAllowed="false"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceReviewerNames"
        :title="'Reviewer'"
        :fieldName="'reviewers'"
        :loading="serviceReviewerNamesLoading"
        :onlyOneAllowed="false"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceReviewAssignerNames"
        :title="'Review Assigner'"
        :fieldName="'reviewAssigners'"
        :loading="serviceReviewAssignerNamesLoading"
        :onlyOneAllowed="false"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceFeesOptions"
        :title="'Fees'"
        :fieldName="'fees'"
        :loading="serviceFeesOptionsLoading"
        :onlyOneAllowed="false"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :options="serviceProductOptions"
        :title="'Has Service Product'"
        :fieldName="'serviceProducts'"
        :loading="serviceProductOptionsLoading"
      />
      <SearchFacetOptions
        :entityType="'Service'"
        :title="'Previous Service ID'"
        :fieldName="'previousServices'"
        :isInputTextBox="true"
      />
      <EventTypeSearchFacets
        :key="eventTypeFacetKey"
      />
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { LookupModules } from '@/store/moduleConfig';
import SearchFacetOptions from '@/components/search/SearchFacetOptions.vue';
import EventTypeSearchFacets from '@/components/search/EventTypeSearchFacets.vue';

export default {
  name: 'ServiceSearchFacets',
  components: {
    EventTypeSearchFacets,
    SearchFacetOptions,
  },
  props: {
    radioButtonFacetKey: {
      type: Number,
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  computed: {
    // Service
    ...mapState('Service', {
      serviceStaffLeadNames: state => state.distinctStaffLeadNames,
      serviceReviewerNames: state => state.distinctReviewerNames,
      serviceReviewAssignerNames: state => state.distinctReviewAssignerNames,
      serviceStaffLeadLoading: state => state.distinctStaffLeadNamesLoading,
      serviceReviewerNamesLoading: state => state.distinctReviewerNamesLoading,
      serviceReviewAssignerNamesLoading: state => state.distinctReviewAssignerNamesLoading,
      previousServiceIdOptions: state => state.distinctPreviousServiceIds,
    }),
    ...mapState(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypeOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.FEE_SEARCH_OPTIONS.namespace, {
      serviceFeesOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.LOCALITIES.namespace, {
      localityLoading: state => state.loading,
    }),
    ...mapState(LookupModules.REVIEW_SEARCH_OPTIONS.namespace, {
      serviceReviewsOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_CATEGORIES.namespace, {
      serviceCategoryOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_TYPES.namespace, {
      serviceTypeOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_STATUSES.namespace, {
      serviceStatusOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SERVICE_PRODUCTS.namespace, {
      serviceProductOptionsLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.FEE_SEARCH_OPTIONS.namespace, {
      serviceFeesOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.LOCALITIES.namespace, {
      localityOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.REVIEW_SEARCH_OPTIONS.namespace, {
      serviceReviewsOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_CATEGORIES.namespace, {
      serviceCategoryOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_TYPES.namespace, {
      serviceTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_STATUSES.namespace, {
      serviceStatusOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SERVICE_PRODUCTS.namespace, {
      serviceProductOptions: 'getActiveOptions',
    }),
    // created this so we do not have key clashes between different facets w/ radiobuttons
    eventTypeFacetKey() {
      return this.radioButtonFacetKey + 400;
    },
    serviceTypeOptionsSortedAlphabetically() {
      const serviceTypeOptionsWithCode = this.serviceTypeOptions.map(sto => ({
        serviceCategoryId: sto.serviceCategoryId,
        serviceCategory: sto.serviceCategory,
        id: sto.id,
        name: `${sto.name} (${sto.serviceCategory.name.replace(/[^A-Z]/g, '')})`,
        sortOrder: sto.sortOrder,
        archiveDate: sto.archiveDate,
      }));
      return serviceTypeOptionsWithCode.sort((a, b) => {
        const textA = a.name;
        const textB = b.name;
        if (textA < textB) return -1;
        if (textA > textB) return 1;
        return 0;
      });
    },
  },
  mounted() {
    this.$store.dispatch('Service/fetchStaffLeadNames');
    this.$store.dispatch('Service/fetchReviewerNames');
    this.$store.dispatch('Service/fetchReviewAssignerNames');
    this.$store.dispatch(`${LookupModules.AGREEMENT_TYPES.namespace}/fetchOptions`,
      LookupModules.AGREEMENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_CATEGORIES.namespace}/fetchOptions`,
      LookupModules.SERVICE_CATEGORIES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_TYPES.namespace}/fetchOptions`,
      LookupModules.SERVICE_TYPES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_STATUSES.namespace}/fetchOptions`,
      LookupModules.SERVICE_STATUSES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_PRODUCTS.namespace}/fetchOptions`,
      LookupModules.SERVICE_PRODUCTS.url);
    this.$store.dispatch(`${LookupModules.FEE_SEARCH_OPTIONS.namespace}/fetchOptions`,
      LookupModules.FEE_SEARCH_OPTIONS.url);
    this.$store.dispatch(`${LookupModules.REVIEW_SEARCH_OPTIONS.namespace}/fetchOptions`,
      LookupModules.REVIEW_SEARCH_OPTIONS.url);
  },

};
</script>
